import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Car, Hash, Ticket, Calendar,
  IndianRupee, FileText, CheckCircle2, Clock, Save, RefreshCw,
  AlertCircle, MapPin, Wrench
} from 'lucide-react';
import { customerAPI, serviceAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// ── Ordinals grouped for coupon grid layout ──
const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th'];
const SERVICE_TYPES = [
  'PMS Amount Discount',
  'Wheel Alignment',
  '5 Wheel Balance',
  'Diagnostic Charge',
  'Upholstery Cleaning',
  'Vehicle Washing Cleaning',
  'Pickup Drop Charges',
  'Day Shift MOS',
  'Night Shift MOS',
];

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{value || '—'}</div>
    </div>
  </div>
);

const ServiceCell = ({ service, selected, onToggle }) => {
  const isUsed = service.status === 'USED';
  return (
    <div
      className={`
        relative rounded-lg border p-2.5 transition-all duration-150 text-center
        ${isUsed
          ? 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800'
          : selected
            ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 dark:border-brand-600 shadow-sm'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300 dark:hover:border-brand-700 cursor-pointer'
        }
      `}
      onClick={() => !isUsed && onToggle(service.name)}
      title={isUsed ? `Claimed: ${service.claimDate ? format(new Date(service.claimDate), 'dd MMM yyyy, hh:mm a') : ''}` : service.name}
    >
      <div className={`text-xs font-medium leading-tight ${isUsed ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
        {service.name}
      </div>
      {isUsed ? (
        <div className="mt-1.5">
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">USED</span>
          {service.claimDate && (
            <div className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 leading-tight">
              {format(new Date(service.claimDate), 'dd/MM/yy')}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-1.5">
          {selected ? (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-400">
              <CheckCircle2 className="w-3 h-3" /> Selected
            </span>
          ) : (
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Available</span>
          )}
        </div>
      )}
    </div>
  );
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCustomer();
  }, [id]);

  const loadCustomer = async () => {
    setLoading(true);
    try {
      const res = await customerAPI.getById(id);
      const c = res.data.data;
      setCustomer(c);
      await loadServices(c.membership_no);
    } catch (err) {
      toast.error('Failed to load customer');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (membershipNo) => {
    try {
      const res = await serviceAPI.getByMembership(membershipNo);
      setServiceData(res.data.data);
      setSelected(new Set());
    } catch {
      toast.error('Failed to load services');
    }
  };

  const toggleService = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.size === 0) {
      toast.error('Please select at least one service to claim');
      return;
    }
    setSaving(true);
    try {
      const res = await serviceAPI.claimServices({
        membershipNo: customer.membership_no,
        couponNo: customer.coupon_no,
        services: [...selected],
      });
      const { claimed, alreadyClaimed } = res.data.data;
      if (claimed.length > 0) toast.success(`${claimed.length} service(s) claimed!`);
      if (alreadyClaimed.length > 0) toast(`${alreadyClaimed.length} service(s) were already claimed`, { icon: '⚠️' });
      await loadServices(customer.membership_no);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save services');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!customer) return null;

  const usedCount = serviceData?.usedServices ?? 0;
  const availCount = serviceData?.availableServices ?? 0;
  const total = serviceData?.totalServices ?? 0;
  const claimPct = total ? Math.round((usedCount / total) * 100) : 0;

  // Group services by ordinal for grid display
  const serviceMap = {};
  serviceData?.services?.forEach(s => {
    serviceMap[s.name] = s;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Back */}
      <button
        onClick={() => navigate('/search')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Search
      </button>

      {/* Customer header */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
              {customer.customer_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{customer.customer_name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
                  {customer.coupon_type}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">#{customer.membership_no}</span>
              </div>
            </div>
          </div>

          {/* Service summary pills */}
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{availCount}</div>
              <div className="text-xs text-gray-500">Available</div>
            </div>
            <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
            <div className="text-center">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">{usedCount}</div>
              <div className="text-xs text-gray-500">Used</div>
            </div>
            <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
            <div className="text-center">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Service utilization</span>
            <span>{claimPct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500" style={{ width: `${claimPct}%` }} />
          </div>
        </div>
      </div>

      {/* Customer info grid */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-brand-500" />
          Customer Information
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <InfoRow icon={Hash} label="Membership No" value={customer.membership_no} />
          <InfoRow icon={Ticket} label="Coupon No" value={customer.coupon_no} />
          <InfoRow icon={Phone} label="Mobile" value={customer.mobile} />
          <InfoRow icon={Car} label="Vehicle No" value={customer.vehicle_no} />
          <InfoRow icon={Car} label="Model" value={customer.model} />
          <InfoRow icon={Calendar} label="Membership Date" value={customer.membership_date} />
          <InfoRow icon={Calendar} label="Expiry Date" value={customer.expiry_date} />
          <InfoRow icon={IndianRupee} label="Amount" value={customer.amount ? `₹${parseFloat(customer.amount).toLocaleString('en-IN')}` : null} />
          <InfoRow icon={FileText} label="Receipt No" value={customer.receipt_no} />
          {customer.address && <InfoRow icon={MapPin} label="Address" value={customer.address} />}
        </div>
      </div>

      {/* Services grid */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-brand-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {customer.coupon_type} — Services
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadServices(customer.membership_no)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
            {selected.size > 0 && (
              <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                {selected.size} service{selected.size !== 1 ? 's' : ''} selected
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || selected.size === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
            >
              {saving
                ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Save className="w-3 h-3" />
              }
              Save Services
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-400" />
            <span className="text-gray-500 dark:text-gray-400">Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-brand-50 dark:bg-brand-900/30 border border-brand-400" />
            <span className="text-gray-500 dark:text-gray-400">Selected</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-950/40 border border-red-400" />
            <span className="text-gray-500 dark:text-gray-400">Used</span>
          </div>
        </div>

        {/* Grid - showing coupon layout like the images */}
        <div className="p-4 overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th className="p-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-left w-16">Visit</th>
                {SERVICE_TYPES.map(type => (
                  <th key={type} className="p-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                    {type}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ORDINALS.map(ord => (
                <tr key={ord}>
                  <td className="p-2">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{ord}</span>
                  </td>
                  {SERVICE_TYPES.map(type => {
                    const svcName = `${ord} ${type}`;
                    const svc = serviceMap[svcName] || { name: svcName, status: 'available', claimDate: null };
                    return (
                      <td key={type} className="p-1">
                        <ServiceCell
                          service={svc}
                          selected={selected.has(svcName)}
                          onToggle={toggleService}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom action bar */}
        {selected.size > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-brand-50 dark:bg-brand-900/20 rounded-b-xl">
            <div className="flex items-center gap-2 text-sm text-brand-700 dark:text-brand-400">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-medium">{selected.size} service{selected.size !== 1 ? 's' : ''} selected for claiming</span>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              Save Services
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
